/** Tríade orçamentária alinhada às colunas Tipo / Classificação / Descrição (R-D). */

export type Triplet = { tipo: string; classif: string; descr: string };

const SEP = "\u0001";

function trimPart(s: string | null | undefined): string {
  return (s ?? "").trim();
}

export function rowToTriplet(
  tipo: string | null | undefined,
  classif: string | null | undefined,
  descr: string | null | undefined
): Triplet {
  return {
    tipo: trimPart(tipo),
    classif: trimPart(classif),
    descr: trimPart(descr),
  };
}

export function keyExact(p: Triplet): string {
  return `${p.tipo}${SEP}${p.classif}${SEP}${p.descr}`;
}

function normalizeToken(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

export function keyNormalized(p: Triplet): string {
  return `${normalizeToken(p.tipo)}|${normalizeToken(p.classif)}|${normalizeToken(p.descr)}`;
}

export function parseExactKey(key: string): Triplet {
  const [tipo, classif, descr] = key.split(SEP);
  return { tipo: tipo ?? "", classif: classif ?? "", descr: descr ?? "" };
}

export type TripletCount = Triplet & { count: number };

export type SpellingDivergence = {
  normalizedKey: string;
  orcamentoVariants: TripletCount[];
  realizadoVariants: TripletCount[];
};

export type TripletAuditResult = {
  orcamentoDistinct: number;
  realizadoDistinct: number;
  bothExact: number;
  onlyOrcamento: TripletCount[];
  onlyRealizado: TripletCount[];
  spellingDivergences: SpellingDivergence[];
  orcamentoRowsAnalisadas: number;
  realizadoRowsAnalisadas: number;
  orcamentoSemTriade: number;
  realizadoSemTriade: number;
};

function countByKey(rows: Triplet[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const p of rows) {
    const k = keyExact(p);
    m.set(k, (m.get(k) ?? 0) + 1);
  }
  return m;
}

function tripletIsEmpty(p: Triplet): boolean {
  return !p.tipo && !p.classif && !p.descr;
}

/**
 * Compara tríades das linhas de orçamento vs realizado (mesmo ano na seleção da UI).
 * Igualdade “exata” = trim nos três campos; divergência de grafia = mesma chave normalizada, sem chave exata comum entre os lados.
 */
export function auditTripletSets(orcamentoRows: Triplet[], realizadoRows: Triplet[]): TripletAuditResult {
  let orcamentoSemTriade = 0;
  let realizadoSemTriade = 0;
  const orcTriplets: Triplet[] = [];
  const reaTriplets: Triplet[] = [];

  for (const p of orcamentoRows) {
    if (tripletIsEmpty(p)) orcamentoSemTriade += 1;
    else orcTriplets.push(p);
  }
  for (const p of realizadoRows) {
    if (tripletIsEmpty(p)) realizadoSemTriade += 1;
    else reaTriplets.push(p);
  }

  const orcCounts = countByKey(orcTriplets);
  const reaCounts = countByKey(reaTriplets);

  const orcExact = new Set(orcCounts.keys());
  const reaExact = new Set(reaCounts.keys());

  let bothExact = 0;
  for (const k of orcExact) {
    if (reaExact.has(k)) bothExact += 1;
  }

  const onlyOrcamento: TripletCount[] = [];
  for (const [k, count] of orcCounts) {
    if (!reaExact.has(k)) {
      const t = parseExactKey(k);
      onlyOrcamento.push({ ...t, count });
    }
  }
  onlyOrcamento.sort((a, b) => b.count - a.count || a.tipo.localeCompare(b.tipo));

  const onlyRealizado: TripletCount[] = [];
  for (const [k, count] of reaCounts) {
    if (!orcExact.has(k)) {
      const t = parseExactKey(k);
      onlyRealizado.push({ ...t, count });
    }
  }
  onlyRealizado.sort((a, b) => b.count - a.count || a.tipo.localeCompare(b.tipo));

  const byNorm = new Map<string, { orc: Map<string, number>; rea: Map<string, number> }>();
  const bump = (norm: string, side: "orc" | "rea", exact: string, n: number) => {
    let bucket = byNorm.get(norm);
    if (!bucket) {
      bucket = { orc: new Map(), rea: new Map() };
      byNorm.set(norm, bucket);
    }
    const m = side === "orc" ? bucket.orc : bucket.rea;
    m.set(exact, (m.get(exact) ?? 0) + n);
  };

  for (const [k, n] of orcCounts) {
    bump(keyNormalized(parseExactKey(k)), "orc", k, n);
  }
  for (const [k, n] of reaCounts) {
    bump(keyNormalized(parseExactKey(k)), "rea", k, n);
  }

  const spellingDivergences: SpellingDivergence[] = [];
  for (const [normKey, { orc, rea }] of byNorm) {
    if (orc.size === 0 || rea.size === 0) continue;
    let hasCommonExact = false;
    for (const ok of orc.keys()) {
      if (rea.has(ok)) {
        hasCommonExact = true;
        break;
      }
    }
    if (hasCommonExact) continue;
    const orcamentoVariants: TripletCount[] = [...orc.entries()]
      .map(([exactKey, count]) => ({ ...parseExactKey(exactKey), count }))
      .sort((a, b) => b.count - a.count);
    const realizadoVariants: TripletCount[] = [...rea.entries()]
      .map(([exactKey, count]) => ({ ...parseExactKey(exactKey), count }))
      .sort((a, b) => b.count - a.count);
    spellingDivergences.push({ normalizedKey: normKey, orcamentoVariants, realizadoVariants });
  }
  spellingDivergences.sort((a, b) => a.normalizedKey.localeCompare(b.normalizedKey));

  return {
    orcamentoDistinct: orcExact.size,
    realizadoDistinct: reaExact.size,
    bothExact,
    onlyOrcamento,
    onlyRealizado,
    spellingDivergences,
    orcamentoRowsAnalisadas: orcamentoRows.length,
    realizadoRowsAnalisadas: realizadoRows.length,
    orcamentoSemTriade,
    realizadoSemTriade,
  };
}
